import { useClerk } from "@clerk/clerk-expo";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type { GetProps } from "tamagui";

import { Link, ScrollView, Text, XStack, YStack } from "@/components";
import { LayoutContext } from "@/components/LayoutProvider";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import Loading from "./Loading";
import OTPForm from "./OTPForm";
import PhoneForm from "./PhoneForm";

function Layout({ children, ...props }: GetProps<typeof YStack>) {
  const { lg } = useContext(LayoutContext);

  if (lg) {
    return (
      <YStack
        height="100%"
        width="100%"
        alignItems="center"
        justifyContent="center"
        flexGrow={1}
        {...props}
      >
        <YStack
          width={400}
          alignItems="center"
          backgroundColor="$backgroundFill"
          padding="$400"
        >
          {children}
        </YStack>
      </YStack>
    );
  }

  return (
    <YStack
      backgroundColor="$backgroundFill"
      height="100%"
      width="100%"
      alignItems="center"
      flexGrow={1}
    >
      <ScrollView
        width="100%"
        padding="$400"
        contentContainerStyle={{ alignItems: "center", height: "100%" }}
      >
        {children}
      </ScrollView>
    </YStack>
  );
}

const EmbeddedLayout = ({ children, ...props }: GetProps<typeof YStack>) => (
  <YStack flex={1} alignItems="center" {...props}>
    {children}
  </YStack>
);

export type FORMS = "phone" | "otp" | "loading";
export type SIGN_IN_FLOW = "sign-in" | "sign-up";

export interface SignInProps {
  conversationId?: string;
  onComplete?: () => void;
  isEmbedded?: boolean;
  /** Called when back button visibility should change (for embedded mode).
   * Parent can use this to control a back button in its own header. */
  onBackButtonChange?: (
    visible: boolean,
    onBackPress: (() => void) | null,
  ) => void;
}

export default function SignIn({
  conversationId,
  onComplete,
  isEmbedded = false,
  onBackButtonChange,
}: SignInProps = {}) {
  const { setShowHeader } = useContext(LayoutContext);

  const clerk = useClerk();
  const router = usePropagatedRouter();
  const startedSignedInRef = useRef(clerk.isSignedIn);

  const signInFlowRef = useRef<SIGN_IN_FLOW>("sign-up");

  const [form, setForm] = useState<FORMS>("phone");
  const [phone, setPhone] = useState("");

  // Memoized callback for back button press
  const handleBackPress = useCallback(() => {
    setForm("phone");
  }, []);

  // Notify parent about back button state when form changes (for embedded mode)
  useEffect(() => {
    if (!onBackButtonChange) return;

    if (form === "otp") {
      // On OTP screen, show back button to go back to phone
      onBackButtonChange(true, handleBackPress);
    } else {
      // On phone or loading screen, hide back button
      onBackButtonChange(false, () => {});
    }
  }, [form, onBackButtonChange, handleBackPress]);

  useEffect(() => {
    // If user was signed in before starting the sign in flow, dismiss to home
    if (startedSignedInRef.current) {
      router.dismissTo("/");
    }
  }, [router]);

  useEffect(() => {
    setShowHeader(false);
    return () => {
      setShowHeader(true);
    };
  }, [setShowHeader]);

  const LayoutWrapper = isEmbedded ? EmbeddedLayout : Layout;

  return (
    <LayoutWrapper $lg={isEmbedded ? { maxWidth: 400 } : undefined}>
      {form === "phone" && (
        <PhoneForm
          phone={phone}
          setPhone={setPhone}
          setForm={setForm}
          signInFlowRef={signInFlowRef}
          isEmbedded={isEmbedded}
        />
      )}
      {form === "otp" && (
        <OTPForm
          phone={phone}
          setForm={setForm}
          signInFlowRef={signInFlowRef}
          isEmbedded={isEmbedded}
        />
      )}
      {form === "loading" ? (
        <YStack
          height="100%"
          width="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Loading
            signInFlowRef={signInFlowRef}
            conversationId={conversationId}
            onComplete={onComplete}
            isEmbedded={isEmbedded}
          />
        </YStack>
      ) : (
        <XStack
          flexGrow={1}
          paddingTop="$100"
          justifyContent="center"
          alignItems="flex-end"
          gap="$100"
          $lg={{ marginTop: "$800" }}
          $maxLg={{ paddingBottom: "$300" }}
        >
          <Link
            href="/privacy"
            size="$sm"
            whiteSpace="nowrap"
            target="_blank"
            color="$textPrimary"
            aria-label="Privacy policy (opens in new tab)"
            dataTrackingId="privacy-policy"
          >
            Privacy policy
          </Link>
          <Text size="$sm">|</Text>
          <Link
            href="/terms"
            size="$sm"
            whiteSpace="nowrap"
            target="_blank"
            color="$textPrimary"
            aria-label="Terms of use (opens in new tab)"
            dataTrackingId="terms-of-use"
          >
            Terms of use
          </Link>
        </XStack>
      )}
    </LayoutWrapper>
  );
}
